import { useMemo } from "react";
import { AlertCircle, CheckCircle2, X } from "lucide-react";
import { compareAtlasClaims } from "@/lib/atlasClaims";
import { prepClaimFeeName } from "@/lib/prepClaims";
import { formatMoney } from "./BillingReportPanel";

// Claims saved with the week store dates as text; turn them back into dates to compare.
const hydrate = (claims = []) => claims.map(claim => ({
  ...claim,
  claimedOn: claim.claimedOn ? new Date(claim.claimedOn) : null,
  appearanceOn: claim.appearanceOn ? new Date(claim.appearanceOn) : null,
}));

const shortDate = (date) => (date ? date.toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "—");

function Section({ tone, title, help, count, children }) {
  const tones = {
    amber: "border-amber-300 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-900",
    sky: "border-sky-200 bg-sky-50 text-sky-900",
  };
  return (
    <section className={`rounded-xl border p-3 ${tones[tone]}`}>
      <h4 className="flex items-center gap-2 text-sm font-bold">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        {title} ({count})
      </h4>
      <p className="mt-0.5 text-xs opacity-80">{help}</p>
      <div className="mt-2 overflow-x-auto rounded-lg border border-white/60 bg-white text-slate-800">{children}</div>
    </section>
  );
}

const th = "px-2.5 py-1.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500";
const td = "px-2.5 py-1.5 align-top text-xs";

export default function AtlasComparePanel({ atlas, entries, actions, fees, prepClaims = [], onJump, onClear, onAddPrep }) {
  const result = useMemo(
    () => compareAtlasClaims(entries, actions, fees, hydrate(atlas?.claims), prepClaims),
    [atlas, entries, actions, fees, prepClaims]
  );
  if (!atlas) return null;

  const jump = (entry) => (
    <button type="button" onClick={() => onJump?.(entry.id)} className="text-left font-semibold text-blue-700 underline hover:text-blue-900">
      {entry.client_name || "Client not named"}
    </button>
  );
  const issues = result.processorOnly.length + result.atlasNotClaimedHere.length + result.amountDiffers.length + result.atlasNoAppearance.length
    + result.prepOnly.length + result.prepAmountDiffers.length;
  const matchedCount = result.matched.length + result.prepMatched.length;

  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm print:hidden">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">ATLAS claims comparison</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            {atlas.fileName} · {atlas.claims.length} ATLAS claim{atlas.claims.length === 1 ? "" : "s"} · uploaded {new Date(atlas.uploadedAt).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}
          </p>
        </div>
        <button type="button" onClick={onClear} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-500 hover:bg-slate-50" title="Remove this comparison from the week">
          <X className="h-3.5 w-3.5" /> Remove
        </button>
      </div>

      <p className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
        <CheckCircle2 className="h-4 w-4" />
        {matchedCount === 1 ? "1 claim matches" : `${matchedCount} claims match`} the processor{result.prepMatched.length > 0 ? ` (${result.prepMatched.length} of them prep claims)` : ""}{issues === 0 ? ", and nothing needs checking." : "."}
      </p>

      {result.processorOnly.length > 0 && (
        <Section tone="red" title="Claimed in the processor, not in ATLAS" count={result.processorOnly.length} help="Marked as claimed here, but no ATLAS claim names this client on this date. Claim it in ATLAS, or change the action.">
          <table className="w-full">
            <thead><tr><th className={th}>Date</th><th className={th}>Client</th><th className={th}>Action</th><th className={`${th} text-right`}>Fee</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {result.processorOnly.map(({ entry, action, fee }) => (
                <tr key={entry.id}><td className={td}>{entry.date}</td><td className={td}>{jump(entry)}</td><td className={td}>{action}</td><td className={`${td} text-right`}>{formatMoney(fee)}</td></tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {result.atlasNotClaimedHere.length > 0 && (
        <Section tone="amber" title="Claimed in ATLAS, not marked claimed here" count={result.atlasNotClaimedHere.length} help="ATLAS has a claim for this appearance, but the processor row is not set to an appearance type, Claimed or Billed and closed.">
          <table className="w-full">
            <thead><tr><th className={th}>Date</th><th className={th}>Client</th><th className={th}>ATLAS</th><th className={th}>Processor action</th><th className={`${th} text-right`}>ATLAS $</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {result.atlasNotClaimedHere.map(({ claim, entry, action }) => (
                <tr key={`${claim.index}-${entry.id}`}><td className={td}>{entry.date}</td><td className={td}>{jump(entry)}</td><td className={td}>{claim.description}</td><td className={td}>{action || "No action set"}</td><td className={`${td} text-right`}>{formatMoney(claim.amount)}</td></tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {result.amountDiffers.length > 0 && (
        <Section tone="amber" title="Amounts differ" count={result.amountDiffers.length} help="Claimed in both, but the ATLAS amount is not the processor's fee.">
          <table className="w-full">
            <thead><tr><th className={th}>Date</th><th className={th}>Client</th><th className={th}>ATLAS</th><th className={`${th} text-right`}>ATLAS $</th><th className={`${th} text-right`}>Processor $</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {result.amountDiffers.map(({ claim, entry, fee }) => (
                <tr key={`${claim.index}-${entry.id}`}><td className={td}>{entry.date}</td><td className={td}>{jump(entry)}</td><td className={td}>{claim.description}</td><td className={`${td} text-right`}>{formatMoney(claim.amount)}</td><td className={`${td} text-right`}>{formatMoney(fee)}</td></tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {result.prepOnly.length > 0 && (
        <Section tone="red" title="Prep claim here, not in ATLAS" count={result.prepOnly.length} help="On this week's preparation claims, but no ATLAS claim names this client (and grant). Claim it in ATLAS, or remove it from the week.">
          <table className="w-full">
            <thead><tr><th className={th}>Client</th><th className={th}>Grant</th><th className={th}>Prep fee</th><th className={`${th} text-right`}>Amount</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {result.prepOnly.map(({ line, amount }) => (
                <tr key={line.id}><td className={`${td} font-semibold`}>{line.client || "Client not named"}</td><td className={td}>{line.grant || "—"}</td><td className={td}>{prepClaimFeeName(line) || "Not chosen"}</td><td className={`${td} text-right`}>{formatMoney(amount)}</td></tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {result.prepAmountDiffers.length > 0 && (
        <Section tone="amber" title="Prep amounts differ" count={result.prepAmountDiffers.length} help="Claimed in both, but the ATLAS amount is not the prep claim's amount.">
          <table className="w-full">
            <thead><tr><th className={th}>Client</th><th className={th}>ATLAS</th><th className={th}>Prep fee</th><th className={`${th} text-right`}>ATLAS $</th><th className={`${th} text-right`}>Prep claim $</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {result.prepAmountDiffers.map(({ claim, line, amount }) => (
                <tr key={`${claim.index}-${line.id}`}><td className={`${td} font-semibold`}>{line.client}</td><td className={td}>{claim.description}</td><td className={td}>{prepClaimFeeName(line) || "Not chosen"}</td><td className={`${td} text-right`}>{formatMoney(claim.amount)}</td><td className={`${td} text-right`}>{formatMoney(amount)}</td></tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {result.atlasNoAppearance.length > 0 && (
        <Section tone="sky" title="In ATLAS, no matching appearance this week" count={result.atlasNoAppearance.length} help="Usually preparation or disbursements claimed after approval, or an appearance from another week. Check each one was meant to be claimed.">
          <table className="w-full">
            <thead><tr><th className={th}>Client</th><th className={th}>ATLAS</th><th className={th}>Grant</th><th className={th}>Why</th><th className={`${th} text-right`}>ATLAS $</th>{onAddPrep && <th className={th}><span className="sr-only">Add</span></th>}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {result.atlasNoAppearance.map(({ claim, reason }) => (
                <tr key={claim.index}><td className={`${td} font-semibold`}>{claim.client}</td><td className={td}>{claim.description}{claim.appearanceOn ? ` (${shortDate(claim.appearanceOn)})` : ""}</td><td className={td}>{claim.grant || "—"}</td><td className={td}>{reason}</td><td className={`${td} text-right`}>{formatMoney(claim.amount)}</td>{onAddPrep && (
                  <td className={`${td} text-right`}>
                    <button type="button" onClick={() => onAddPrep(claim)} className="whitespace-nowrap rounded-lg border border-purple-300 bg-purple-50 px-2 py-1 text-[11px] font-bold text-purple-800 hover:bg-purple-100">Add as prep claim</button>
                  </td>
                )}</tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}
